/**
 * Where problems are and how to get there (W2-12a checker-runtime; DESIGN.md §4.18, §5.10): the time of a problem
 * (its beat, or its seconds through the tempo map), its line, the "Go" target (`nav.reveal` seeks, selects and
 * focuses the line; problems without a time open the inspector field or the Assets panel), "Ignore here" (stored in
 * the document with Core.doc.ignoreProblem, undoable), the Problems panel filters and the two orders problems are
 * listed in (most severe first in the panel; time order for next / previous).
 *
 * Pure helpers over services, shared by the problems service, the Problems panel and the timeline marks, so the
 * panel also works over any ProblemsService (contract-only fakes in tests).
 */

import type { AppServices } from '../../app/registry/types';
import type { CommandArgs } from '../../app/registry/commands';
import { SEVERITY_RANK } from '../../core/check/runner';
import type { CheckCategory, Problem, Severity } from '../../core/check/types';
import { Core } from '../../core/commands/index';
import { buildTempoMap } from '../../core/doc/container';
import type { ChartDoc, DocIndexes, LineId } from '../../core/model/types';
import { Beats } from '../../core/time/beat';
import { formatBeat } from '../../core/time/beatSyntax';
import type { Beat, BeatFormatStyle, TempoMap } from '../../core/time/types';
import type { MessageRef } from '../../core/util/types';
import type { DocStore } from '../../state/types';
import { m } from './checker.messages';

// ---------------------------------------------------------------------------------------------------------------
// Time and place
// ---------------------------------------------------------------------------------------------------------------

/** Tempo map of the open chart: the engine's, else built from the document. */
export function tempoOf(
  s: Pick<AppServices, 'engine'> | null,
  store: Pick<DocStore, 'doc' | 'idx'> | null,
): TempoMap | null {
  const engine = s?.engine ?? null;
  if (engine !== null) return engine.tempo;
  if (store === null) return null;
  try {
    return buildTempoMap(store.doc, store.idx);
  } catch {
    return null;
  }
}

/** Beat of a problem as f64 (its beat, else its seconds through the tempo map); null without a time. */
export function problemBeatF64(p: Problem, tempo: Pick<TempoMap, 'beatF64At'> | null): number | null {
  if (p.at.beat !== undefined) return Beats.toF64(p.at.beat);
  if (p.at.sec !== undefined && tempo !== null && Number.isFinite(p.at.sec)) return tempo.beatF64At(p.at.sec);
  return null;
}

/** Exact beat of a problem (seconds are converted on the 1/1920 grid); null without a time. */
export function problemBeat(p: Problem, tempo: Pick<TempoMap, 'beatAt'> | null): Beat | null {
  if (p.at.beat !== undefined) return p.at.beat;
  if (p.at.sec !== undefined && tempo !== null && Number.isFinite(p.at.sec)) {
    const b = tempo.beatAt(p.at.sec);
    return Beats.cmp(b, Beats.ZERO) < 0 ? Beats.ZERO : b;
  }
  return null;
}

/** The line a problem is about: `at.line`, else the first of `at.lines`, else the line of its first note. */
export function problemLine(p: Problem, doc: Pick<ChartDoc, 'notes' | 'lines'> | null): LineId | null {
  if (p.at.line !== undefined) return p.at.line;
  const first = p.at.lines?.[0];
  if (first !== undefined) return first;
  const note = p.at.notes?.[0];
  if (note !== undefined && doc !== null) return doc.notes.get(note)?.line ?? null;
  return null;
}

/** Whether a problem concerns a line (its line, one of its lines, or the line of one of its notes). */
export function problemOnLine(p: Problem, line: LineId, doc: Pick<ChartDoc, 'notes' | 'lines'> | null): boolean {
  if (p.at.line === line) return true;
  if (p.at.lines?.includes(line) === true) return true;
  if (doc !== null && p.at.notes !== undefined) {
    for (const n of p.at.notes) if (doc.notes.get(n)?.line === line) return true;
  }
  return false;
}

/** Arguments of `nav.reveal` for a problem with a time, else null. */
export function revealArgs(
  p: Problem,
  tempo: Pick<TempoMap, 'beatAt'> | null,
  doc: Pick<ChartDoc, 'notes' | 'lines'> | null,
): CommandArgs['nav.reveal'] | null {
  const beat = problemBeat(p, tempo);
  if (beat === null) return null;
  const line = problemLine(p, doc);
  const notes = p.at.notes;
  const keys = p.at.keys;
  return {
    beat: { w: beat.w, n: beat.n, d: beat.d },
    ...(line !== null ? { line } : {}),
    ...(notes !== undefined && notes.length > 0 ? { notes: [...notes] } : {}),
    ...(keys !== undefined && keys.length > 0 ? { keys: [...keys] } : {}),
  };
}

export type GoServices = Pick<AppServices, 'store' | 'engine' | 'registry' | 'ui' | 'audio'>;

/** Chart seconds of the playhead (0 without a transport). */
export function playheadSec(s: Pick<AppServices, 'audio'> | null): number {
  if (s === null) return 0;
  try {
    const t = s.audio.transport.chartTime();
    return Number.isFinite(t) ? t : 0;
  } catch {
    return 0;
  }
}

/** The playhead as an exact beat (so revealing a line there does not move the playhead). */
function playheadBeat(s: Pick<AppServices, 'audio'>, tempo: Pick<TempoMap, 'beatAt'> | null): Beat {
  if (tempo === null) return Beats.ZERO;
  const b = tempo.beatAt(playheadSec(s));
  return Beats.cmp(b, Beats.ZERO) < 0 ? Beats.ZERO : b;
}

/** Whether "Go" can take the user anywhere for this problem. */
export function canGo(p: Problem): boolean {
  return (
    p.at.beat !== undefined ||
    p.at.sec !== undefined ||
    p.at.field !== undefined ||
    p.at.asset !== undefined ||
    p.at.line !== undefined ||
    (p.at.lines?.length ?? 0) > 0
  );
}

/**
 * "Go": seek, select and focus the line (`nav.reveal`). A problem without a time makes its line active where the
 * playhead is (no seek), then opens its inspector field, or shows the Assets panel for a file.
 */
export async function revealProblem(s: GoServices, p: Problem): Promise<boolean> {
  const store = s.store;
  const tempo = tempoOf(s, store);
  const args = revealArgs(p, tempo, store?.doc ?? null);
  if (args !== null) return s.registry.run('nav.reveal', args, 'button');
  const line = problemLine(p, store?.doc ?? null);
  const here = playheadBeat(s, tempo);
  const lineArgs = line === null ? null : { beat: { w: here.w, n: here.n, d: here.d }, line };
  if (p.at.field !== undefined) {
    if (lineArgs !== null) await s.registry.run('nav.reveal', lineArgs, 'button');
    return s.registry.run('inspector.focusField', { field: p.at.field }, 'button');
  }
  if (lineArgs !== null) return s.registry.run('nav.reveal', lineArgs, 'button');
  if (p.at.asset !== undefined) {
    s.ui.showPanel('assets');
    return true;
  }
  return false;
}

/** "line name · 12:1/4" for the list. */
export function locationText(
  p: Problem,
  doc: Pick<ChartDoc, 'notes' | 'lines'> | null,
  idx: Pick<DocIndexes, 'lineIndex'> | null,
  tempo: Pick<TempoMap, 'beatAt'> | null,
  style: BeatFormatStyle,
  t: (key: string, params?: Readonly<Record<string, string | number>>) => string,
): string {
  const line = problemLine(p, doc);
  const beat = problemBeat(p, tempo);
  let lineText: string | null = null;
  if (line !== null && doc !== null) {
    const name = doc.lines.get(line)?.name.trim() ?? '';
    lineText = name !== '' ? name : idx !== null ? `#${String(idx.lineIndex(line))}` : `#${String(line)}`;
  }
  const beatText = beat === null ? null : formatBeat(beat, style === 'bar' ? 'colon' : style);
  if (lineText !== null && beatText !== null) return t(m.location, { line: lineText, beat: beatText });
  return lineText ?? beatText ?? t(m.locationChart);
}

// ---------------------------------------------------------------------------------------------------------------
// Ignore here
// ---------------------------------------------------------------------------------------------------------------

/** Stores the suppression in the document (one undoable step). Returns false when nothing changed. */
export function ignoreProblemInDoc(store: DocStore, p: Pick<Problem, 'id' | 'rule'>): boolean {
  if (store.doc.editor.ignoredProblems.includes(p.id)) return false;
  const out = store.transact(
    { label: { key: m.txnIgnore, params: { rule: p.rule } }, command: 'problems.ignore' },
    (tx) => {
      Core.doc.ignoreProblem(tx, { key: p.id, ignore: true });
    },
  );
  return out.ok && out.txn !== null;
}

/** Removes suppressions from the document (one undoable step). */
export function unignoreInDoc(store: DocStore, keys: readonly string[]): boolean {
  const present = keys.filter((k) => store.doc.editor.ignoredProblems.includes(k));
  if (present.length === 0) return false;
  const out = store.transact(
    { label: { key: m.txnUnignore, params: { count: present.length } }, command: 'problems.unignoreAll' },
    (tx) => {
      for (const key of present) Core.doc.ignoreProblem(tx, { key, ignore: false });
    },
  );
  return out.ok && out.txn !== null;
}

/** Rule id of an "Ignore here" key (`${rule}:${entity}:${id}`). */
export function ruleOfKey(key: string): string {
  const i = key.indexOf(':');
  return i < 0 ? key : key.slice(0, i);
}

// ---------------------------------------------------------------------------------------------------------------
// Filters and order
// ---------------------------------------------------------------------------------------------------------------

export interface ProblemFilter {
  readonly severities: readonly Severity[];
  readonly categories: readonly CheckCategory[];
  /** 'line': only problems of the active line. */
  readonly scope: 'chart' | 'line';
  readonly query: string;
}

export const ALL_SEVERITIES: readonly Severity[] = Object.freeze(['fatal', 'error', 'warning', 'tip']);
export const ALL_CATEGORIES: readonly CheckCategory[] = Object.freeze(['phira', 'gameplay', 'style']);

export const DEFAULT_FILTER: ProblemFilter = Object.freeze({
  severities: ALL_SEVERITIES,
  categories: ALL_CATEGORIES,
  scope: 'chart',
  query: '',
});

/** Most severe first, then time (problems without a time first), rule, id. */
export function bySeverity(beatOf: (p: Problem) => number | null): (a: Problem, b: Problem) => number {
  const time = byTime(beatOf);
  return (a, b) => {
    const s = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
    if (s !== 0) return s;
    return time(a, b);
  };
}

/** Time order (problems without a time first), then severity, rule, id. */
export function byTime(beatOf: (p: Problem) => number | null): (a: Problem, b: Problem) => number {
  return (a, b) => {
    const ta = beatOf(a) ?? -Infinity;
    const tb = beatOf(b) ?? -Infinity;
    if (ta !== tb) return ta < tb ? -1 : 1;
    const s = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
    if (s !== 0) return s;
    return a.rule < b.rule ? -1 : a.rule > b.rule ? 1 : a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  };
}

/** Lower-case words of a search query. */
export function queryWords(q: string): string[] {
  return q
    .toLowerCase()
    .split(/\s+/)
    .filter((w) => w.length > 0);
}

/** Whether a problem matches every word of the query (rule id, WHAT, WHY, fix labels, location). */
export function matchesQuery(p: Problem, words: readonly string[], text: (p: Problem) => string): boolean {
  if (words.length === 0) return true;
  const hay = `${p.rule} ${text(p)}`.toLowerCase();
  return words.every((w) => hay.includes(w));
}

/** The problems a filter shows (not sorted). */
export function filterProblems(
  list: readonly Problem[],
  f: ProblemFilter,
  opts: {
    readonly activeLine: LineId | null;
    readonly doc: Pick<ChartDoc, 'notes' | 'lines'> | null;
    readonly text: (p: Problem) => string;
  },
): Problem[] {
  const sev = new Set(f.severities);
  const cat = new Set(f.categories);
  const words = queryWords(f.query);
  return list.filter((p) => {
    if (!sev.has(p.severity) || !cat.has(p.category)) return false;
    if (f.scope === 'line') {
      if (opts.activeLine === null || !problemOnLine(p, opts.activeLine, opts.doc)) return false;
    }
    return matchesQuery(p, words, opts.text);
  });
}

/** Counts per severity. */
export function countBySeverity(list: readonly Problem[]): Record<Severity, number> {
  const c: Record<Severity, number> = { fatal: 0, error: 0, warning: 0, tip: 0 };
  for (const p of list) c[p.severity]++;
  return c;
}

/** Text of a MessageRef for search (WHAT, WHY and fix labels). */
export function searchText(p: Problem, tr: (ref: MessageRef) => string): string {
  const parts = [tr(p.message)];
  if (p.why !== undefined) parts.push(tr(p.why));
  for (const f of p.fixes) parts.push(tr(f.label));
  return parts.join(' ');
}
